/**
 * MontaTech v2 - useCanvas Hook
 * 
 * Gerencia o estado e interações do canvas com paginação e layout responsivo
 * Suporta dois modos:
 * - Thumbnails: mostra objetos coletados em grid
 * - Nesting: mostra resultado do cálculo em blocos
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { CanvasTransform, MontaTechCurveData, MontaTechCurve, MontaTechProfile, RFigure, HoverInfo, MaterialBlock, MaterialResult } from '../types';
import { sendToTerminal } from '../../../../lib/terminalLogger';
import { groupCurves, type GroupedCurve } from '../../../../lib/montatech/utils/grouping';

export interface UseCanvasOptions {
  curveData: MontaTechCurveData | null;
  nestingResult: RFigure[] | null;
  materials: MaterialResult[] | null;  // ✅ Estrutura hierárquica do backend
  maximumY: number | null;
  profile: MontaTechProfile | null;
  containerRef: React.RefObject<HTMLDivElement>;
  blocksPerRow?: number;  // ✅ Override do blocksPerRow do perfil
  renderTraceId?: string | null;
  renderTraceStateSetAtPerf?: number | null;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
}

export interface UseCanvasReturn {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  transform: CanvasTransform;
  isDragging: boolean;
  hoverInfo: HoverInfo | null;
  pagination: PaginationInfo;
  visibleCurves: MontaTechCurve[];
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  setZoom: (zoom: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  goToPage: (page: number) => void;
  setItemsPerPage: (count: number) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleWheel: (e: React.WheelEvent) => void;
}

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;
const ZOOM_STEP = 1.2;

// Layout config
const GRID_GAP = 12;
const PADDING = 16;
const LABEL_HEIGHT = 36; // Espaço para labels abaixo
const MIN_CARD_SIZE = 100;
const MAX_CARD_SIZE = 180;

// Cores para figuras (cicla entre essas)
const FIGURE_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#ef4444', // red
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
];

const NESTING_RESULT_COLOR = '#38bdf8';

type PreviewFigure = RFigure & {
  materialIndex?: number;
  lineIndex?: number;
  sheetIndex?: number;
  localX?: number;
  localY?: number;
  sheetOffsetX?: number;
  sheetOffsetY?: number;
};

const MM_EPSILON = 0.0001;
const PREVIEW_BEZIER_SAMPLES = 4;
const PREVIEW_SHAPE_STROKE_WIDTH = 0.7;
const PREVIEW_SHAPE_LINE_CAP: CanvasLineCap = 'round';
const PREVIEW_SHAPE_LINE_JOIN: CanvasLineJoin = 'round';
const PREVIEW_BLOCKS_PER_ROW = 3;
const PREVIEW_GROUP_BY_BLOCK = true;
const PREVIEW_APPLY_OTSTUP_OFFSET = false;
const PREVIEW_CLIP_TO_MATERIAL_BOUNDS = true;
const PREVIEW_BLOCK_GAP_RATIO = 0.05;

type CurvePointMm = { x: number; y: number };
type CurvePathMm = CurvePointMm[];

function nearlyEqualMm(a: number, b: number): boolean {
  return Math.abs(a - b) <= MM_EPSILON;
}

function resolveAbsoluteAxisMm(
  rawValueMm: number,
  localValueMm?: number,
  sheetOffsetMm?: number
): number {
  if (localValueMm == null || sheetOffsetMm == null || sheetOffsetMm <= 0) {
    return rawValueMm;
  }

  const localPlusOffset = localValueMm + sheetOffsetMm;

  if (nearlyEqualMm(rawValueMm, localPlusOffset)) {
    return rawValueMm;
  }

  if (nearlyEqualMm(rawValueMm, localValueMm)) {
    return localPlusOffset;
  }

  if (rawValueMm + MM_EPSILON < sheetOffsetMm) {
    return rawValueMm + sheetOffsetMm;
  }

  return rawValueMm;
}

function resolveFigureBlockIndex(
  figure: PreviewFigure,
  materialHeightMm: number
): number | null {
  const resolvedY = resolveAbsoluteAxisMm(figure.y, figure.localY, figure.sheetOffsetY);
  const inferredFromAbsoluteY =
    materialHeightMm > MM_EPSILON && resolvedY >= 0
      ? Math.floor(resolvedY / materialHeightMm)
      : null;

  const blockFromSheetOffset =
    materialHeightMm > MM_EPSILON && figure.sheetOffsetY != null && figure.sheetOffsetY > MM_EPSILON
      ? Math.floor(figure.sheetOffsetY / materialHeightMm)
      : null;

  const blockFromMaterialIndex =
    figure.materialIndex != null && figure.materialIndex >= 0
      ? figure.materialIndex
      : null;

  const blockFromLineIndex =
    figure.lineIndex != null && figure.lineIndex >= 0
      ? figure.lineIndex
      : null;

  const looksAbsoluteY =
    inferredFromAbsoluteY != null &&
    materialHeightMm > MM_EPSILON &&
    resolvedY >= materialHeightMm + MM_EPSILON;

  if (blockFromSheetOffset != null) {
    return blockFromSheetOffset;
  }

  if (blockFromMaterialIndex != null) {
    if (!looksAbsoluteY || inferredFromAbsoluteY == null || inferredFromAbsoluteY === blockFromMaterialIndex) {
      return blockFromMaterialIndex;
    }
  }

  if (blockFromLineIndex != null) {
    if (!looksAbsoluteY || inferredFromAbsoluteY == null || inferredFromAbsoluteY === blockFromLineIndex) {
      return blockFromLineIndex;
    }
  }

  return inferredFromAbsoluteY;
}

function normalizeLocalAxisToBlockMm(valueMm: number, materialHeightMm: number): number {
  if (materialHeightMm <= MM_EPSILON) {
    return valueMm;
  }

  let normalized = valueMm % materialHeightMm;
  if (normalized < 0) {
    normalized += materialHeightMm;
  }

  return normalized;
}

function resolveFigurePlacementBlockIndex(
  figure: PreviewFigure,
  materialHeightMm: number
): number {
  if (figure.materialIndex != null && figure.materialIndex >= 0) {
    return figure.materialIndex;
  }

  return Math.max(0, resolveFigureBlockIndex(figure, materialHeightMm) ?? 0);
}

function resolveMaterialLocalXMm(figure: PreviewFigure): number {
  if (figure.localX != null) {
    return figure.localX + (figure.sheetOffsetX ?? 0);
  }
  return resolveAbsoluteAxisMm(figure.x, figure.localX, figure.sheetOffsetX);
}

function resolveMaterialLocalYMm(figure: PreviewFigure): number {
  if (figure.localY != null) {
    return figure.localY + (figure.sheetOffsetY ?? 0);
  }
  return resolveAbsoluteAxisMm(figure.y, figure.localY, figure.sheetOffsetY);
}

function resolveFigurePlacementMm(
  figure: PreviewFigure,
  materialHeightMm: number,
  leftFieldMm: number = 0,
  downFieldMm: number = 0,
  otstupOffsetMm: number = 0
) {
  const blockIndex = resolveFigurePlacementBlockIndex(figure, materialHeightMm);

  let resolvedX: number;
  let resolvedY: number;

  if (figure.materialIndex != null && figure.materialIndex >= 0) {
    resolvedX = resolveMaterialLocalXMm(figure);
    resolvedY = resolveMaterialLocalYMm(figure);
  } else {
    const blockOffsetY = blockIndex * materialHeightMm;
    resolvedX = resolveAbsoluteAxisMm(figure.x, figure.localX, figure.sheetOffsetX);
    resolvedY = resolveAbsoluteAxisMm(figure.y, figure.localY, figure.sheetOffsetY);

    if (resolvedY < materialHeightMm && blockIndex > 0) {
      const inferredBlock = Math.floor(resolvedY / materialHeightMm);
      if (inferredBlock !== blockIndex) {
        resolvedY = resolvedY + blockOffsetY;
      }
    }
  }

  const anchorX = resolvedX + leftFieldMm - otstupOffsetMm;
  const anchorY = resolvedY + downFieldMm - otstupOffsetMm;
  
  // Para renderização, mantemos a coordenada absoluta
  const localAnchorY = anchorY;

  return {
    resolvedX,
    resolvedY,
    blockIndex,
    anchorX,
    anchorY,
    localAnchorY,
  };
}

function computeBlockCount(usedLengthMm: number, materialHeightMm: number): number {
  if (materialHeightMm <= MM_EPSILON) {
    return 1;
  }

  return Math.max(1, Math.ceil(Math.max(usedLengthMm, 0) / materialHeightMm));
}

function normalizeBlocksPerRow(blocksPerRow: number): number {
  return Math.max(1, Math.floor(blocksPerRow));
}

function computeBlockRows(blockCount: number, blocksPerRow: number): number {
  return Math.ceil(Math.max(1, blockCount) / normalizeBlocksPerRow(blocksPerRow));
}

function computeBlockGapMm(materialWidthMm: number): number {
  return Math.max(1, materialWidthMm * PREVIEW_BLOCK_GAP_RATIO);
}

function getBlockOriginXmm(blockIndex: number, materialWidthMm: number, blockGapMm: number, blocksPerRow: number): number {
  const col = Math.max(0, blockIndex) % normalizeBlocksPerRow(blocksPerRow);
  return col * (materialWidthMm + blockGapMm);
}

function getBlockOriginYmm(blockIndex: number, materialHeightMm: number, blockGapMm: number, blocksPerRow: number): number {
  const row = Math.floor(Math.max(0, blockIndex) / normalizeBlocksPerRow(blocksPerRow));
  return row * (materialHeightMm + blockGapMm);
}

function mapWorldToCanvasPx(
  worldX: number,
  worldY: number,
  materialWidthMm: number,
  materialHeightMm: number,
  blockGapMm: number,
  scale: number,
  blocksPerRow: number,
  groupByBlock: boolean,
  originX: number,
  originY: number,
  preferredBlockIndex?: number | null,
  displayBlockIndexMap?: Map<number, number>
) {
  const safeMaterialH = Math.max(MM_EPSILON, materialHeightMm);
  
  // ✅ CORREÇÃO: Usar preferredBlockIndex como prioridade se fornecido
  // Isso evita recalcular o bloco baseado em coordenadas Y
  let blockIndex: number;
  
  if (preferredBlockIndex != null && preferredBlockIndex >= 0) {
    blockIndex = preferredBlockIndex;
  } else {
    // Fallback: inferir do Y apenas se não tivermos preferredBlockIndex
    blockIndex = Math.floor(worldY / safeMaterialH);
    if (blockIndex < 0) blockIndex = 0;
  }

  // ✅ CORREÇÃO: Calcular a posição relativa ao bloco
  // Se worldY é absoluta (inclui offset do bloco), precisamos subtrair para obter localY
  // Mas se worldY já está no range do bloco (0..altura), usamos diretamente
  let localY: number;
  
  // Verificar se worldY parece absoluta (maior que altura do material) ou local
  const minYForBlock = blockIndex * safeMaterialH;
  const maxYForBlock = (blockIndex + 1) * safeMaterialH;
  
  if (worldY >= minYForBlock && worldY < maxYForBlock) {
    // worldY é absoluta, converter para local
    localY = worldY - minYForBlock;
  } else if (worldY < safeMaterialH) {
    // worldY parece ser local (no range 0..altura)
    localY = worldY;
  } else {
    // Fallback: normalizar
    localY = worldY % safeMaterialH;
    if (localY < 0) localY += safeMaterialH;
  }

  const displayBlockIndex = displayBlockIndexMap?.get(blockIndex) ?? blockIndex;
  const blockOriginX = getBlockOriginXmm(displayBlockIndex, materialWidthMm, blockGapMm, blocksPerRow);
  const blockOriginY = getBlockOriginYmm(displayBlockIndex, materialHeightMm, blockGapMm, blocksPerRow);
  const localX = worldX + blockOriginX;
  const localYInCanvas = blockOriginY + (safeMaterialH - localY);

  return {
    x: originX + localX * scale,
    y: originY + localYInCanvas * scale,
    blockIndex,
    displayBlockIndex,
    localY,
  };
}

function computeUsedLengthMm(
  figures: PreviewFigure[],
  materialHeightMm: number,
  downFieldMm: number,
  maximumY: number | null
): number {
  if (figures.length === 0) {
    return Math.max(maximumY ?? 0, materialHeightMm);
  }

  let derivedAbsoluteMaxY = 0;
  let hintedBlockCount = 1;

  figures.forEach((figure) => {
    const placement = resolveFigurePlacementMm(figure, materialHeightMm, 0, downFieldMm);
    if (placement.anchorY > derivedAbsoluteMaxY) {
      derivedAbsoluteMaxY = placement.anchorY;
    }

    hintedBlockCount = Math.max(hintedBlockCount, placement.blockIndex + 1);
  });

  const usedFromFigures = Math.max(0, derivedAbsoluteMaxY + downFieldMm);
  const usedFromHints = Math.max(0, hintedBlockCount * Math.max(materialHeightMm, 0));
  const usedLength = Math.max(maximumY ?? 0, Math.max(usedFromFigures, usedFromHints));

  return Math.max(usedLength, materialHeightMm);
}

function getCurveConvFactorMm(curve: MontaTechCurve): number {
  if (curve.maxLength > 0) {
    if (curve.maxLength < 0.01) {
      return curve.maxLength * 25.4;
    }

    return (curve.maxLength * 25.4) / 65530.0;
  }

  const fallbackInches = Math.max(curve.width, curve.height);
  return Math.max(0.0000001, (fallbackInches * 25.4) / 65530.0);
}

function getCurveBoundsMm(curve: MontaTechCurve): { minX: number; minY: number; maxX: number; maxY: number } {
  const rawPaths = buildCurvePolylinePathsMm(curve);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  rawPaths.forEach((path) => {
    path.forEach((point) => {
        if (point.x < minX) minX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.x > maxX) maxX = point.x;
        if (point.y > maxY) maxY = point.y;
    });
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    const fallbackWidth = Math.max(curve.width * 25.4, 1);
    const fallbackHeight = Math.max(curve.height * 25.4, 1);
    return { minX: 0, minY: 0, maxX: fallbackWidth, maxY: fallbackHeight };
  }

  return { minX, minY, maxX, maxY };
}

function rotatePointMm(x: number, y: number, rotationRadians: number): CurvePointMm {
  const cos = Math.cos(rotationRadians);
  const sin = Math.sin(rotationRadians);
  return {
    x: x * cos + y * sin,
    y: y * cos - x * sin
  };
}

function getPathsBoundsMm(paths: CurvePathMm[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  paths.forEach((path) => {
    path.forEach((point) => {
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
    });
  });

  return { minX, minY, maxX, maxY };
}

function getCurvePreviewGeometryMm(
  curve: MontaTechCurve,
  rotationDegrees = 0
): { paths: CurvePathMm[]; bounds: { minX: number; minY: number; maxX: number; maxY: number } } {
  const rawPaths = buildCurvePolylinePathsMm(curve);
  if (rawPaths.length === 0) {
    return { paths: [], bounds: getCurveBoundsMm(curve) };
  }

  const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;
  if (normalizedRotation === 0) {
    return {
      paths: rawPaths,
      bounds: getPathsBoundsMm(rawPaths)
    };
  }

  const rotationRadians = (rotationDegrees * Math.PI) / 180;
  const rotatedPaths = rawPaths.map((path) =>
    path.map((point) => rotatePointMm(point.x, point.y, rotationRadians))
  );

  return {
    paths: rotatedPaths,
    bounds: getPathsBoundsMm(rotatedPaths)
  };
}

function evaluateCubicBezier(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;

  return {
    x: (uuu * p0.x) + (3 * uu * t * p1.x) + (3 * u * tt * p2.x) + (ttt * p3.x),
    y: (uuu * p0.y) + (3 * uu * t * p1.y) + (3 * u * tt * p2.y) + (ttt * p3.y)
  };
}

function buildCurvePolylinePathsMm(curve: MontaTechCurve, samplesPerBezier = PREVIEW_BEZIER_SAMPLES): Array<Array<{ x: number; y: number }>> {
  const convFactorMm = getCurveConvFactorMm(curve);
  const paths: Array<Array<{ x: number; y: number }>> = [];
  const safeSamples = Math.max(2, Math.min(16, samplesPerBezier));

  curve.paths.forEach((path) => {
    if (!path.segments.length) return;

    const points: Array<{ x: number; y: number }> = [];

    path.segments.forEach((seg, index) => {
      const nextSeg = path.segments[(index + 1) % path.segments.length];
      const p0 = { x: seg.SNPx * convFactorMm, y: seg.SNPy * convFactorMm };
      const p1 = { x: seg.SCPx * convFactorMm, y: seg.SCPy * convFactorMm };
      const p2 = { x: seg.ECPx * convFactorMm, y: seg.ECPy * convFactorMm };
      const p3 = { x: nextSeg.SNPx * convFactorMm, y: nextSeg.SNPy * convFactorMm };

      for (let sample = 0; sample <= safeSamples; sample += 1) {
        if (index > 0 && sample === 0) continue;
        points.push(evaluateCubicBezier(p0, p1, p2, p3, sample / safeSamples));
      }
    });

    if (points.length > 1) {
      paths.push(points);
    }
  });

  return paths;
}

export function useCanvas({ curveData, nestingResult, materials, maximumY, profile, containerRef, blocksPerRow: blocksPerRowProp, renderTraceId, renderTraceStateSetAtPerf }: UseCanvasOptions): UseCanvasReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastLoggedRenderTraceIdRef = useRef<string | null>(null);
  
  const [transform, setTransform] = useState<CanvasTransform>({ zoom: 1, panX: 0, panY: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPageState] = useState(12);
  const [containerSize, setContainerSize] = useState({ width: 400, height: 300 });
  
  // Modo de renderização - usa materials[] se disponível, senão nestingResult
  const hasNestingData = (materials && materials.length > 0) || (nestingResult && nestingResult.length > 0);
  const renderMode = hasNestingData ? 'nesting' : 'thumbnails';
  
  // ✅ Usar blocksPerRow do prop (override) ou do perfil ou o default
  const effectiveBlocksPerRow = blocksPerRowProp ?? profile?.blocksPerRow ?? PREVIEW_BLOCKS_PER_ROW;
  
  // Converter microns para mm
  const micronsToMm = (microns: number) => microns / 1000;

  // Reset page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [curveData?.count]);

  // Track container size for responsive layout
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
        
        // Auto-adjust items per page based on available space
        const availableWidth = width - PADDING * 2;
        const availableHeight = height - PADDING * 2 - 50; // 50 for pagination
        
        const cols = Math.max(2, Math.floor((availableWidth + GRID_GAP) / (MIN_CARD_SIZE + GRID_GAP)));
        const rows = Math.max(2, Math.floor((availableHeight + GRID_GAP) / (MIN_CARD_SIZE + LABEL_HEIGHT + GRID_GAP)));
        
        const newItemsPerPage = cols * rows;
        setItemsPerPageState(newItemsPerPage);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [containerRef]);

  // Calcular blocos de material para modo nesting
  // ✅ CORREÇÃO: Usar materials[] do backend e respeitar materialIndex
  const materialBlocks = useMemo((): MaterialBlock[] => {
    const tBlocks = performance.now();
    if (!profile) return [];

    const sheetHeight = micronsToMm(profile.materialHeight);
    const downFieldMm = micronsToMm(profile.downField);
    if (sheetHeight <= 0) return [];

    // ✅ CORREÇÃO PRIORITÁRIA: Se temos materials[], usar estrutura hierárquica diretamente
    if (materials && materials.length > 0) {
      sendToTerminal(`📦 [NESTING/BLOCKS] Usando materials[] do backend — ${materials.length} materials`);

      // Criar blocos baseados nos materiais do backend
      const blocks: MaterialBlock[] = materials.map((material, materialIdx) => {
        const blockIndex = material.materialIndex ?? materialIdx;
        const figures: PreviewFigure[] = [];
        
        material.lines.forEach((line, lineIdx) => {
          line.sheets.forEach((sheet, sheetIdx) => {
            sheet.figures.forEach((figure) => {
              // ✅ CORREÇÃO: Garantir que materialIndex está definido e coordenadas são absolutas
              figures.push({
                ...figure,
                materialIndex: blockIndex,
                lineIndex: figure.lineIndex ?? lineIdx,
                sheetIndex: figure.sheetIndex ?? sheetIdx,
              });
            });
          });
        });
        
        return {
          index: blockIndex + 1, // 1-based para exibição
          startY: blockIndex * sheetHeight,
          endY: (blockIndex + 1) * sheetHeight,
          figures
        };
      });

      const tEnd = performance.now();
      sendToTerminal(`✅ [NESTING/BLOCKS] Blocos criados do materials[] em ${Math.round(tEnd - tBlocks)}ms — ${blocks.length} blocos, ${blocks.reduce((s, b) => s + b.figures.length, 0)} figuras`);

      return blocks;
    }
    
    // Fallback: calcular a partir do array flat (nestingResult)
    if (nestingResult && nestingResult.length > 0) {
      const allFigures = (nestingResult as PreviewFigure[]).map((figure, idx) => {
        // ✅ CORREÇÃO: Garantir materialIndex a partir dos dados da figura
        const f = { ...figure };
        if (f.materialIndex == null) {
          // Inferir do Y se não tiver materialIndex
          f.materialIndex = Math.floor(f.y / sheetHeight);
        }
        return f;
      });

      sendToTerminal('⚠️ [NESTING/BLOCKS] Fallback: calculando blocos via figures[]', {
        figuresCount: allFigures.length
      });

      // Encontrar o materialIndex máximo para determinar quantos blocos
      const maxMaterialIndex = Math.max(...allFigures.map(f => f.materialIndex ?? 0), 0);
      const blockCount = maxMaterialIndex + 1;
      
      const blocks: MaterialBlock[] = Array.from({ length: blockCount }, (_, index) => ({
        index: index + 1,
        startY: index * sheetHeight,
        endY: (index + 1) * sheetHeight,
        figures: []
      }));

      // Distribuir figuras nos blocos baseado em materialIndex
      allFigures.forEach((figure) => {
        const blockIndex = figure.materialIndex ?? 0;
        
        if (!blocks[blockIndex]) {
          blocks[blockIndex] = {
            index: blockIndex + 1,
            startY: blockIndex * sheetHeight,
            endY: (blockIndex + 1) * sheetHeight,
            figures: []
          };
        }

        blocks[blockIndex].figures.push(figure);
      });

      return blocks.filter(b => b.figures.length > 0);
    }

    return [];
  }, [materials, nestingResult, profile, maximumY]);

  // Grouping: agrupa curvas identicas (mesmas dims + topologia) para preview
  const groupedCurves = useMemo((): GroupedCurve[] => {
    if (!curveData?.curves || curveData.curves.length === 0) return [];
    return groupCurves(curveData.curves, 2);
  }, [curveData?.curves]);

  // Pagination calculations (thumbnails mode)
  const pagination = useMemo((): PaginationInfo => {
    // Em modo nesting, pagina por blocos
    if (renderMode === 'nesting') {
      const totalItems = materialBlocks.length;
      const blocksPerPage = normalizeBlocksPerRow(effectiveBlocksPerRow);
      const totalPages = Math.max(1, Math.ceil(totalItems / blocksPerPage));
      const safePage = Math.min(currentPage, totalPages);
      const startIndex = (safePage - 1) * blocksPerPage;
      const endIndex = Math.min(startIndex + blocksPerPage, totalItems);

      return {
        currentPage: safePage,
        totalPages,
        itemsPerPage: blocksPerPage,
        totalItems,
        startIndex,
        endIndex
      };
    }

    // Modo thumbnails — pagina pelos grupos (nao curvas individuais)
    const totalItems = groupedCurves.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    return {
      currentPage: safePage,
      totalPages,
      itemsPerPage,
      totalItems,
      startIndex,
      endIndex
    };
  }, [groupedCurves.length, currentPage, itemsPerPage, renderMode, materialBlocks.length]);

  const visibleCurves = useMemo(() => {
    if (!curveData?.curves) return [];
    // Extrair curvas representantes dos grupos visiveis
    return groupedCurves
      .slice(pagination.startIndex, pagination.endIndex)
      .map(g => g.curve);
  }, [curveData?.curves, groupedCurves, pagination.startIndex, pagination.endIndex]);

  // Grupos visiveis (com quantidade) para renderizacao de thumbnails
  const visibleGroups = useMemo((): GroupedCurve[] => {
    return groupedCurves.slice(pagination.startIndex, pagination.endIndex);
  }, [groupedCurves, pagination.startIndex, pagination.endIndex]);

  // Zoom actions
  const zoomIn = useCallback(() => {
    setTransform(prev => ({ ...prev, zoom: Math.min(prev.zoom * ZOOM_STEP, MAX_ZOOM) }));
  }, []);

  const zoomOut = useCallback(() => {
    setTransform(prev => ({ ...prev, zoom: Math.max(prev.zoom / ZOOM_STEP, MIN_ZOOM) }));
  }, []);

  const resetTransform = useCallback(() => {
    setTransform({ zoom: 1, panX: 0, panY: 0 });
    setHoverInfo(null);
    setCurrentPage(1);
  }, []);

  const setZoom = useCallback((zoom: number) => {
    setTransform(prev => ({ ...prev, zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)) }));
  }, []);

  // Pagination actions
  const nextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages));
  }, [pagination.totalPages]);

  const prevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, pagination.totalPages)));
  }, [pagination.totalPages]);

  const setItemsPerPage = useCallback((count: number) => {
    setItemsPerPageState(Math.max(1, count));
    setCurrentPage(1);
  }, []);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - transform.panX, y: e.clientY - transform.panY });
    }
  }, [transform.panX, transform.panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setTransform(prev => ({ ...prev, panX: e.clientX - dragStart.x, panY: e.clientY - dragStart.y }));
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Posição do cursor relativa ao canvas
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    const factor = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;

    setTransform(prev => {
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * factor));
      const scale = newZoom / prev.zoom;
      // Ajustar pan para manter o ponto sob o cursor fixo
      const newPanX = cursorX - scale * (cursorX - prev.panX);
      const newPanY = cursorY - scale * (cursorY - prev.panY);
      return { zoom: newZoom, panX: newPanX, panY: newPanY };
    });
  }, []);

  // Canvas resize
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resizeObserver = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = container;
      const dpr = window.devicePixelRatio || 1;
      
      canvas.width = clientWidth * dpr;
      canvas.height = clientHeight * dpr;
      canvas.style.width = `${clientWidth}px`;
      canvas.style.height = `${clientHeight}px`;
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [containerRef]);

  // Force re-render when curveData or nestingResult changes
  const [renderKey, setRenderKey] = useState(0);
  
  useEffect(() => {
    if (curveData || nestingResult) {
      setRenderKey(k => k + 1);
    }
  }, [curveData, nestingResult]);

  // Helper: desenhar uma curva com transformação
  // Usa a mesma convenção visual no thumbnail e no nesting:
  // o canto superior direito do bounding box transformado ancora em (anchorRightPx, anchorTopPx).
  const drawCurve = useCallback((
    ctx: CanvasRenderingContext2D,
    curve: MontaTechCurve,
    anchorRightPx: number,
    anchorTopPx: number,
    rotation: number,  // Rotação em graus
    strokeColor: string,
    scale: number      // Escala em pixels/mm
  ) => {
    const { paths, bounds } = getCurvePreviewGeometryMm(curve, rotation);
    if (paths.length === 0) {
      return;
    }

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = PREVIEW_SHAPE_STROKE_WIDTH;
    ctx.lineCap = PREVIEW_SHAPE_LINE_CAP;
    ctx.lineJoin = PREVIEW_SHAPE_LINE_JOIN;

    paths.forEach((path) => {
      if (path.length < 2) return;

      ctx.beginPath();
      ctx.moveTo(
        anchorRightPx + (path[0].x - bounds.maxX) * scale,
        anchorTopPx - (path[0].y - bounds.maxY) * scale
      );

      for (let i = 1; i < path.length; i += 1) {
        ctx.lineTo(
          anchorRightPx + (path[i].x - bounds.maxX) * scale,
          anchorTopPx - (path[i].y - bounds.maxY) * scale
        );
      }

      ctx.stroke();
    });
    
    ctx.restore();
  }, []);

  // Render
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    // Ensure canvas size is correct
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    // Reset transform and clear
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, width, height);

    // ============ MODO NESTING ============
    // Debug: verificar estado antes de renderizar
    if (renderMode === 'nesting') {
      sendToTerminal(`🎨 [CANVAS] Modo NESTING detectado — ${materialBlocks.length} blocos, curves=${!!curveData}, maximumY=${maximumY?.toFixed(1) ?? 'N/A'}`);
    }
    
    if (renderMode === 'nesting' && materialBlocks.length > 0 && profile && curveData) {
      const t0 = performance.now();

      // Index de curvas por ID para lookup O(1) em vez de O(n) find()
      const curveMap = new Map(curveData.curves.map(c => [c.index, c]));

      const materialWidthMm = micronsToMm(profile.materialWith);
      const sheetHeightMm = micronsToMm(profile.materialHeight);
      const leftFieldMm = micronsToMm(profile.leftField);
      const rightFieldMm = micronsToMm(profile.rightField);
      const upFieldMm = micronsToMm(profile.upField);
      const downFieldMm = micronsToMm(profile.downField);
      const otstupMm = micronsToMm(profile.otstup);
      const otstupOffsetMm = PREVIEW_APPLY_OTSTUP_OFFSET ? otstupMm : 0;
      const usedLengthMm = computeUsedLengthMm(materialBlocks.flatMap(block => block.figures as PreviewFigure[]), sheetHeightMm, downFieldMm, maximumY);

      // Log de distribuição de blocos
      sendToTerminal(`📦 [NESTING/BLOCKS] Distribuição: ${materialBlocks.length} blocos, sheet=${sheetHeightMm.toFixed(0)}×${materialWidthMm.toFixed(0)}mm`);
      
      // Blocos visíveis (cada bloco = uma folha de material)
      const visibleBlocks = materialBlocks.slice(pagination.startIndex, pagination.endIndex);
      const visibleBlockCount = Math.max(1, visibleBlocks.length);
      const visibleBlocksPerRow = Math.min(normalizeBlocksPerRow(effectiveBlocksPerRow), visibleBlockCount);
      const visibleBlockRows = computeBlockRows(visibleBlockCount, visibleBlocksPerRow);
      const blockGapMm = computeBlockGapMm(materialWidthMm);
      const columnsUsed = Math.min(visibleBlockCount, visibleBlocksPerRow);

      const totalWidthMm = materialWidthMm * columnsUsed + blockGapMm * Math.max(0, columnsUsed - 1);
      const totalHeightMm = sheetHeightMm * visibleBlockRows + blockGapMm * Math.max(0, visibleBlockRows - 1);
      const availableWidth = Math.max(1, width - PADDING * 2);
      const availableHeight = Math.max(1, height - PADDING * 2 - 32);
      const scale = Math.min(availableWidth / totalWidthMm, availableHeight / totalHeightMm);
      const totalWidthPx = totalWidthMm * scale;
      const totalHeightPx = totalHeightMm * scale;
      const startX = PADDING + (availableWidth - totalWidthPx) / 2;
      const startY = PADDING + 18 + (availableHeight - totalHeightPx) / 2;
      const displayBlockIndexMap = new Map<number, number>(
        visibleBlocks.map((block, displayIndex) => [block.index - 1, displayIndex])
      );
      
      // Apply transform (zoom/pan do usuário)
      ctx.save();
      ctx.translate(transform.panX, transform.panY);
      ctx.scale(transform.zoom, transform.zoom);
      
      // Desenhar cada bloco (folha de material)
      visibleBlocks.forEach((block, blockIdx) => {
        const blockOriginXmm = getBlockOriginXmm(blockIdx, materialWidthMm, blockGapMm, visibleBlocksPerRow);
        const blockOriginYmm = getBlockOriginYmm(blockIdx, sheetHeightMm, blockGapMm, visibleBlocksPerRow);
        const blockX = startX + blockOriginXmm * scale;
        const blockY = startY + blockOriginYmm * scale;
        const blockW = materialWidthMm * scale;
        const blockH = sheetHeightMm * scale;
        const blockStartY = (block.index - 1) * sheetHeightMm;
        const usedInBlockMm = Math.max(0, Math.min(sheetHeightMm, usedLengthMm - blockStartY));
        
        // Título do bloco (fora do clip)
        ctx.fillStyle = '#94a3b8';
        ctx.font = 'bold 12px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`#${block.index}`, blockX + blockW / 2, blockY - 6);
        
        ctx.fillStyle = 'rgba(200, 200, 200, 0.08)';
        ctx.fillRect(blockX, blockY, blockW, blockH);

        if (usedInBlockMm > 0) {
          const usedTopY = sheetHeightMm - usedInBlockMm;
          ctx.fillStyle = 'rgba(22, 163, 74, 0.08)';
          ctx.strokeStyle = 'rgba(22, 163, 74, 0.55)';
          ctx.lineWidth = 1.2;
          ctx.fillRect(blockX, blockY + usedTopY * scale, blockW, usedInBlockMm * scale);
          ctx.strokeRect(blockX, blockY + usedTopY * scale, blockW, usedInBlockMm * scale);
        }
        
        // Borda do material
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 2;
        ctx.strokeRect(blockX, blockY, blockW, blockH);

        const marginStroke = 'rgba(255, 165, 0, 0.55)';
        ctx.strokeStyle = marginStroke;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        if (leftFieldMm > 0) {
          ctx.beginPath();
          ctx.moveTo(blockX + leftFieldMm * scale, blockY);
          ctx.lineTo(blockX + leftFieldMm * scale, blockY + blockH);
          ctx.stroke();
        }
        if (rightFieldMm > 0) {
          ctx.beginPath();
          ctx.moveTo(blockX + blockW - rightFieldMm * scale, blockY);
          ctx.lineTo(blockX + blockW - rightFieldMm * scale, blockY + blockH);
          ctx.stroke();
        }
        if (upFieldMm > 0) {
          ctx.beginPath();
          ctx.moveTo(blockX, blockY + upFieldMm * scale);
          ctx.lineTo(blockX + blockW, blockY + upFieldMm * scale);
          ctx.stroke();
        }
        if (downFieldMm > 0) {
          ctx.beginPath();
          ctx.moveTo(blockX, blockY + blockH - downFieldMm * scale);
          ctx.lineTo(blockX + blockW, blockY + blockH - downFieldMm * scale);
          ctx.stroke();
        }
        ctx.setLineDash([]);
        
        // CLIPPING: Limitar desenho das figuras à área do bloco
        if (PREVIEW_CLIP_TO_MATERIAL_BOUNDS) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(blockX, blockY, blockW, blockH);
          ctx.clip();
        }
        
        // DEBUG: Log de renderização de nesting
        if (blockIdx === 0 && block.figures.length > 0) {
          const fig0 = block.figures[0] as PreviewFigure;
          const curve0 = curveMap.get(fig0.qFigure);
          const placement0 = resolveFigurePlacementMm(fig0, sheetHeightMm, leftFieldMm, downFieldMm);
          // Converter INCHES → MM para exibição correta
          const curve0WidthMm = curve0 ? curve0.width * 25.4 : 0;
          const curve0HeightMm = curve0 ? curve0.height * 25.4 : 0;
          sendToTerminal(`🎨 [NESTING/RENDER] Bloco ${block.index}: ${block.figures.length} figs, scale=${scale.toFixed(4)}, block=${blockW.toFixed(0)}×${blockH.toFixed(0)}px, fig0=[${fig0.qFigure}] pos=(${placement0.resolvedX.toFixed(1)},${placement0.resolvedY.toFixed(1)})mm`);
        }
        
        // Desenhar figuras deste bloco nas POSIÇÕES ABSOLUTAS
        block.figures.forEach(figure => {
          const previewFigure = figure as PreviewFigure;
          // Lookup O(1) via Map
          const curve = curveMap.get(previewFigure.qFigure);
          if (!curve) return;
          
          // IMPORTANTE: curve.width/height estão em INCHES (do CorelDRAW)!
          // Converter para MM: inches × 25.4 = mm
          const placement = resolveFigurePlacementMm(previewFigure, sheetHeightMm, leftFieldMm, downFieldMm, otstupOffsetMm);
          const materialBlockIndex = block.index - 1;
          const mappedPoint = mapWorldToCanvasPx(
            placement.anchorX,
            placement.anchorY,
            materialWidthMm,
            sheetHeightMm,
            blockGapMm,
            scale,
            visibleBlocksPerRow,
            PREVIEW_GROUP_BY_BLOCK,
            startX,
            startY,
            materialBlockIndex,
            displayBlockIndexMap
          );
          const figX = mappedPoint.x;
          const figY = mappedPoint.y;
          
          drawCurve(ctx, curve, figX, figY, previewFigure.rotation, NESTING_RESULT_COLOR, scale);
        });
        
        // Fechar clipping deste bloco
        if (PREVIEW_CLIP_TO_MATERIAL_BOUNDS) {
          ctx.restore();
        }
      });
      
      ctx.restore();

      const renderMs = Math.round(performance.now() - t0);
      const totalFigures = materialBlocks.reduce((sum, b) => sum + b.figures.length, 0);
      const avgMs = totalFigures > 0 ? (renderMs / totalFigures).toFixed(2) : 'N/A';
      sendToTerminal(`⏱️ [CANVAS/PERF] Nesting render: ${totalFigures} figuras, ${materialBlocks.length} blocos em ${renderMs}ms (${avgMs}ms/fig)`);
      if (renderTraceId && lastLoggedRenderTraceIdRef.current !== renderTraceId) {
        lastLoggedRenderTraceIdRef.current = renderTraceId;
        sendToTerminal('🖼️ [CANVAS] Primeiro render efetivo do preview nesting', {
          requestId: renderTraceId,
          totalFigures,
          blocks: materialBlocks.length,
          renderMs,
          elapsedFromStateSetMs:
            typeof renderTraceStateSetAtPerf === 'number'
              ? Math.round(performance.now() - renderTraceStateSetAtPerf)
              : null,
        });
      }

      return;
    }

    // ============ MODO THUMBNAILS ============
    
    // Empty state
    if (!curveData || visibleCurves.length === 0) {
      ctx.fillStyle = '#475569';
      ctx.font = '14px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Selecione objetos e clique em "Coletar"', width / 2, height / 2);
      return;
    }

    // Apply transform
    ctx.save();
    ctx.translate(transform.panX, transform.panY);
    ctx.scale(transform.zoom, transform.zoom);

    // Calculate responsive grid
    const availableWidth = width - PADDING * 2;
    const availableHeight = height - PADDING * 2 - 50; // 50 for pagination bar
    
    const count = visibleCurves.length;
    
    // Calcular número de colunas baseado no tamanho disponível
    const cols = Math.max(2, Math.min(6, Math.ceil(Math.sqrt(count * (availableWidth / availableHeight)))));
    const rows = Math.ceil(count / cols);
    
    // Calcular tamanho de cada célula
    const cellWidth = Math.min(MAX_CARD_SIZE, (availableWidth - GRID_GAP * (cols - 1)) / cols);
    const cellHeight = Math.min(MAX_CARD_SIZE + LABEL_HEIGHT, (availableHeight - GRID_GAP * (rows - 1)) / rows);
    
    // Centralizar o grid
    const gridWidth = cols * cellWidth + (cols - 1) * GRID_GAP;
    const gridHeight = rows * cellHeight + (rows - 1) * GRID_GAP;
    const startX = PADDING + (availableWidth - gridWidth) / 2;
    const startY = PADDING + (availableHeight - gridHeight) / 2;

    // Render cada grupo de curvas
    visibleGroups.forEach((group, idx) => {
      const curve = group.curve;
      const col = idx % cols;
      const row = Math.floor(idx / cols);

      const cellX = startX + col * (cellWidth + GRID_GAP);
      const cellY = startY + row * (cellHeight + GRID_GAP);

      // Área disponível para o desenho (excluindo labels)
      const drawAreaSize = Math.min(cellWidth, cellHeight - LABEL_HEIGHT) - 16; // 16 padding interno

      const curveGeometry = getCurvePreviewGeometryMm(curve, 0);
      const curveBoundsMm = curveGeometry.bounds;
      const boundsWidthMm = Math.max(curveBoundsMm.maxX - curveBoundsMm.minX, 1);
      const boundsHeightMm = Math.max(curveBoundsMm.maxY - curveBoundsMm.minY, 1);
      const aspectRatio = boundsWidthMm / boundsHeightMm;
      let drawW: number;
      let drawH: number;

      if (aspectRatio > 1) {
        // Mais largo que alto
        drawW = drawAreaSize;
        drawH = drawAreaSize / aspectRatio;
      } else {
        // Mais alto que largo
        drawH = drawAreaSize;
        drawW = drawAreaSize * aspectRatio;
      }

      // Centralizar no card
      const drawLeft = cellX + (cellWidth - drawW) / 2;
      const drawTop = cellY + (cellHeight - LABEL_HEIGHT - drawH) / 2;
      const thumbScale = Math.min(drawW / boundsWidthMm, drawH / boundsHeightMm);
      const scaledWidth = boundsWidthMm * thumbScale;
      const scaledHeight = boundsHeightMm * thumbScale;
      const shapeLeft = drawLeft + (drawW - scaledWidth) / 2;
      const shapeTop = drawTop + (drawH - scaledHeight) / 2;
      const shapeAnchorRight = shapeLeft + scaledWidth;

      // Background card
      ctx.fillStyle = '#1e293b';
      ctx.beginPath();
      ctx.roundRect(cellX, cellY, cellWidth, cellHeight, 6);
      ctx.fill();

      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Bounding box (cor neutra fixa, padding maior para não colar no objeto)
      const boxPad = 8;
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(shapeLeft - boxPad, shapeTop - boxPad, scaledWidth + boxPad * 2, scaledHeight + boxPad * 2);

      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1;
      ctx.strokeRect(shapeLeft - boxPad, shapeTop - boxPad, scaledWidth + boxPad * 2, scaledHeight + boxPad * 2);

      const groupColor = FIGURE_COLORS[idx % FIGURE_COLORS.length];

      drawCurve(ctx, curve, shapeAnchorRight, shapeTop, 0, groupColor, thumbScale);

      // Labels
      const labelY = cellY + cellHeight - 8;

      // ID + quantidade (mesmo formato do C#: "ID: 265  x110")
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 11px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      const idLabel = group.quantity > 1
        ? `ID: ${curve.index}   x${group.quantity}`
        : `ID: ${curve.index}`;
      ctx.fillText(idLabel, cellX + cellWidth / 2, labelY);

      // Dimensões em inches
      ctx.fillStyle = '#64748b';
      ctx.font = '9px Inter, system-ui, sans-serif';
      ctx.fillText(
        `${(curve.width * 25.4).toFixed(1)} × ${(curve.height * 25.4).toFixed(1)} mm`,
        cellX + cellWidth / 2,
        labelY - 12
      );
    });

    ctx.restore();
  }, [curveData, visibleCurves, visibleGroups, transform, containerRef, pagination.startIndex, pagination.endIndex, containerSize, renderKey, renderMode, materialBlocks, profile, drawCurve, nestingResult, materials, maximumY, renderTraceId, renderTraceStateSetAtPerf]);

  return {
    canvasRef,
    transform,
    isDragging,
    hoverInfo,
    pagination,
    visibleCurves,
    zoomIn,
    zoomOut,
    resetTransform,
    setZoom,
    nextPage,
    prevPage,
    goToPage,
    setItemsPerPage,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel
  };
}
